import * as dayjs from 'dayjs';
import { TimeZone } from '../../time/timezone.js';
import { MapUtils } from '../../utility/map-utils.js';
import { DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { ISyncEventWithClient, NotificationEvent, SyncEventSource } from '../sync-event.js';
import { CustomerProfile } from './customer-profile.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { VersionRange } from '../core/version-range.js';

export class ClientUpdatedEvent extends NotificationEvent implements ISyncEventWithClient {
  static readonly TYPE_ID: string = '50c5848532b54ff3b791ba32840b8573';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly uniqueId: string,
    public readonly creatorId: string | null,
    public readonly deviceId: string | null,
    public readonly creationDate: dayjs.Dayjs,
    public readonly source: SyncEventSource,
    public readonly seen: boolean,
    public readonly client: CustomerProfile,
    public readonly clientId: string,
  ) {
    super();
  }

  get $typeId(): string {
    return ClientUpdatedEvent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, ClientUpdatedEvent.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): ClientUpdatedEvent {
    return new ClientUpdatedEvent(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.getOrNull<string>(map, 'creatorId'),
        MapUtils.getOrNull<string>(map, 'deviceId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        toEnumFromString(MapUtils.get<string>(map, 'source'), SyncEventSource),
        MapUtils.get<boolean>(map, 'seen'),
        context.fromData<CustomerProfile>(MapUtils.getRaw(map, 'client') as SerializedData),
        MapUtils.get<string>(map, 'clientId'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'deviceId': this.deviceId,
      'creationDate': context.encodeDate(this.creationDate),
      'source': fromEnumAsString(this.source),
      'clientId': this.clientId,
      'client': context.toData(this.client),
      'seen': this.seen,
    };
  }

  clone(): this {
    return new ClientUpdatedEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        this.seen,
        this.client.clone(),
        this.clientId,
    ) as this;
  }

  isSame(other: ClientUpdatedEvent): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.deviceId === this.deviceId &&
        other.creationDate === this.creationDate &&
        other.source === this.source &&
        other.clientId === this.clientId &&
        isSameData(other.client, this.client) &&
        other.seen === this.seen;
  }

  markAsSeen(): this {
    return new ClientUpdatedEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        true,
        this.client.clone(),
        this.clientId,
    ) as this;
  }
}

export class ClientUpdatedEventBuilder extends DataModelBuilder<ClientUpdatedEvent> {
  build(context: SerializationContext, map: SerializedData): ClientUpdatedEvent {
    return ClientUpdatedEvent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(ClientUpdatedEvent.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(ClientUpdatedEvent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    /**
     * Version 2:
     *  - add `deviceId` of type: string | null
     */
    if(version < 2) {
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
